import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetEauApplications } from 'src/hooks/applications/get-applications.hook';
import { useDeleteEauApplication } from 'src/hooks/applications/delete-application-by-id.hook';

import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer/';
import { EauTable } from './components/eau-table';
import { ChangeEau } from './components/change-eau';

import { EAU_MODALS } from './eau.constants';
import { EauWrapper } from './eau.styles';
import { IEauTableItem } from './components/eau-table/eau-table.interfaces';

const Eau = () => {
  const { t } = useTranslation('applications');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [applicationToEdit, setApplicationToEdit] = useState<IEauTableItem | null>(null);

  const currentCompanyId = useGetCurrentCompanyId();
  const { collection, paginationConfig, onTableChange, loading } = useGetEauApplications(
    currentCompanyId
  );

  const handleCreateApplication = () => {
    setActiveModal(EAU_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setApplicationToEdit(null);
  };

  const deleteEauApplication = useDeleteEauApplication({
    onSuccess: closeModal,
  });

  const deleteApplication = () => {
    deleteEauApplication({
      variables: { id: applicationToEdit?._id },
    });
    setApplicationToEdit(null);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteApplication();
  };

  const isCreate = activeModal === EAU_MODALS.create;
  const isEdit = activeModal === EAU_MODALS.edit;

  return (
    <EauWrapper>
      <DrawerComponent
        title={t(`eAU.labels.${isCreate ? 'addNew' : 'editTitle'}`)}
        subtitle={t(`eAU.labels.${isCreate ? 'addNewSubtitle' : 'editSubtitle'}`)}
        onClose={closeModal}
        visible={isEdit || isCreate}
        destroyOnClose
      >
        <ChangeEau onClose={closeModal} id={applicationToEdit?._id} />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === EAU_MODALS.delete}
        title={t('eAU.deleteModal.title')}
        subTitle={t('eAU.deleteModal.subtitle')}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <div>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('labels.eau')}
        </Typography>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.applications}
          action={PERMISSION_ACTIONS.create}
        >
          <Button
            type="primary"
            ghost
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={handleCreateApplication}
          >
            {t('eAU.buttons.add')}
          </Button>
        </PermissionCheck>
      </div>
      <EauTable
        dataSource={collection}
        paginationConfig={paginationConfig}
        onTableChange={onTableChange}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setApplicationToEdit(record);
        }}
        loading={loading}
      />
    </EauWrapper>
  );
};

export default Eau;
